import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {hoverSelector} from '../../../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Renderer, RendererOf} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {DropdownItemCheckbox} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemCheckbox';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {rotate360Animation} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserTheme} from '../../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {navigationLineLabelTextStyle} from '../../../common/navigation/navigationLineLabel';
import {WorkspaceLineMoreButton} from './workspaceLineMoreButton';

/*
 * Props.
 */

export interface WorkspaceLineMoreProps {
  /** Whether this line is disabled (for offline mode). */
  isDisabled?: boolean;
  /** Does this item support a menu? */
  renderMenu?: RendererOf<PopoverTransferProps>;
  /** The number to display on the badge. */
  badgeValue?: number;
  /** Should the counter be bolder. */
  isUnread?: boolean;
  /** Whether the guided demo is currently active */
  isGuidedDemoActive?: boolean;
  /** The reason why the current line should be in a loading state. */
  loadingType?: WorkspaceLoadingTypesEnum;
  /** Whether we should show the offline icon. */
  shouldShowOfflineIcon?: boolean;
  /** Is the workspace in edit mode. */
  isEditingWorkspace?: boolean;
  /** Should the checkbox be selected if we're in editing mode. */
  isChecked?: boolean;
  /** Whether the checkbox is disabled when editing workspaces. */
  isEditorCheckboxDisabled?: boolean;
  isEditingWorkspaceButton?: boolean;
  shouldRemoveBadgeLeftSpacing?: boolean;
}

export enum WorkspaceLoadingTypesEnum {
  IMPORT = 'import',
  BACKFILL_CUSTOM_VIEW = 'backfill_custom_view',
}

/*
 * Style.
 */

interface BadgeStyleProps {
  $isUnread?: boolean;
  $isMoreOpen: boolean;
  $hasMoreMenu: boolean;
  $shouldRemoveLeftPadding?: boolean;
}

export const StyledWorkspaceLineMoreBadgeDiv = styled.div<BadgeStyleProps>`
  grid-area: right;
  align-self: center;

  min-width: 20px;
  border-radius: 9px;

  color: ${(p) => p.theme.item};

  font-weight: ${(p) => (p.$isUnread ? fontWeights.bold : fontWeights.normal)};
  text-align: center;

  ${navigationLineLabelTextStyle};
  ${(p) => maybeAddBadgeHoverStyles(p)};
  ${(p) => maybeAddRarelyViewedBadgeStyles(p)};
`;

const StyledEditingWorkspaceButtonDiv = styled.div`
  grid-area: right;
  align-self: center;
  padding: 0 3px 0 5px;

  color: ${(p) => p.theme.item};
`;

function maybeAddRarelyViewedBadgeStyles(p: BadgeStyleProps) {
  if (p.$shouldRemoveLeftPadding) {
    return css`
      padding-right: 5px;
    `;
  }
  return css`
    padding: 0 5px;
  `;
}

function maybeAddBadgeHoverStyles(props: BadgeStyleProps) {
  // No "more" menu? Never hide.
  if (!props.$hasMoreMenu) {
    return null;
  }

  // More is open: hide
  if (props.$isMoreOpen) {
    return css`
      display: none;
    `;
  }

  // Otherwise, hide on hover so only "more" appears.
  return css`
    ${hoverSelector} {
      display: none;
    }
  `;
}

const StyledBadgeValueSpan = styled.span`
  font-size: ${fontSizes.verySmall};
  /* Baseline align to the main label. */
  vertical-align: baseline;
`;

const StyledMoreDiv = styled.div`
  grid-area: right;
  align-self: center;
`;

const StyledOfflineIcon = styled(Icon)`
  margin-right: 4px;
`;

interface SpinnerStyleProps {
  $hasMenu: boolean;
}
const StyledSpinnerDiv = styled.div<SpinnerStyleProps>`
  display: block;
  grid-area: right;
  align-self: center;
  margin-right: 4px;

  ${rotate360Animation(2)};

  ${hoverSelector} {
    display: ${(p) => (p.$hasMenu ? 'none' : 'block')};
  }
`;

/*
 * Component.
 */

export const WorkspaceLineMore: FC<WorkspaceLineMoreProps> = (props) => {
  const theme = useUserTheme();
  if (props.shouldShowOfflineIcon) {
    return renderOfflineIcon(theme);
  }

  if (!props.renderMenu) {
    return maybeRenderBadge(props, theme, {isMoreOpen: false, hasRenderMenu: false});
  }

  return (
    <StyledMoreDiv>
      <PopoverCoordinator
        renderButton={(isOpen) => renderBadgeOrMoreButton(props, theme, isOpen, Boolean(props.renderMenu))}
        renderModal={props.renderMenu}
      />
    </StyledMoreDiv>
  );
};

/*
 * Helpers.
 */

function renderBadgeOrMoreButton(
  props: WorkspaceLineMoreProps,
  theme: UserTheme,
  isMoreOpen: boolean,
  hasRenderMenu: boolean,
) {
  return (
    <>
      {maybeRenderBadge(props, theme, {isMoreOpen, hasRenderMenu})}
      <WorkspaceLineMoreButton isMoreOpen={isMoreOpen} theme={theme} />
    </>
  );
}

const ImportInProgress = () => <Trans id="importspinner-tooltip">An import is in progress</Trans>;
const CustomViewBackfillInProgress = () => (
  <Trans id="customview-backfill-tooltip">Conversations are being loaded in the view</Trans>
);

const loadingTypeMap: Record<WorkspaceLoadingTypesEnum, Renderer> = {
  [WorkspaceLoadingTypesEnum.IMPORT]: ImportInProgress,
  [WorkspaceLoadingTypesEnum.BACKFILL_CUSTOM_VIEW]: CustomViewBackfillInProgress,
};

interface RenderBadgeOptions {
  isMoreOpen: boolean;
  hasRenderMenu: boolean;
}

function maybeRenderBadge(props: WorkspaceLineMoreProps, theme: UserTheme, options: RenderBadgeOptions) {
  const {loadingType} = props;
  const {isMoreOpen, hasRenderMenu} = options;

  const shouldDisplayLoader = !(hasRenderMenu && isMoreOpen);

  // In case of import.
  if (loadingType && shouldDisplayLoader) {
    return (
      <StyledSpinnerDiv $hasMenu={hasRenderMenu}>
        <TooltipCoordinator render={loadingTypeMap[loadingType]}>
          <Icon name="repeatCircleSmall" color={theme.itemIcon} />
        </TooltipCoordinator>
      </StyledSpinnerDiv>
    );
  }

  if (props.isEditingWorkspace) {
    return (
      <StyledWorkspaceLineMoreBadgeDiv
        $isUnread={false}
        $isMoreOpen={false}
        $hasMoreMenu={false}
        $shouldRemoveLeftPadding={props.shouldRemoveBadgeLeftSpacing}
      >
        <DropdownItemCheckbox
          isChecked={props.isChecked}
          isDisabled={props.isEditorCheckboxDisabled}
          shouldRemoveLeftMargin={props.shouldRemoveBadgeLeftSpacing}
        />
      </StyledWorkspaceLineMoreBadgeDiv>
    );
  }

  if (props.isEditingWorkspaceButton) {
    return (
      <StyledEditingWorkspaceButtonDiv>
        <Icon name="new" color={theme.palette.blue.shade40} />
      </StyledEditingWorkspaceButtonDiv>
    );
  }

  // If no value was provided, or the guided demo is active, don't render anything.
  if (!props.badgeValue || props.isGuidedDemoActive || props.isDisabled) {
    return null;
  }

  return (
    <StyledWorkspaceLineMoreBadgeDiv
      $isUnread={props.isUnread}
      $isMoreOpen={isMoreOpen}
      $hasMoreMenu={Boolean(props.renderMenu)}
    >
      <StyledBadgeValueSpan>{props.badgeValue}</StyledBadgeValueSpan>
    </StyledWorkspaceLineMoreBadgeDiv>
  );
}

function renderOfflineIcon(theme: UserTheme) {
  return (
    <StyledMoreDiv>
      <StyledOfflineIcon name="offlineFilledSmall" color={theme.headerMore} />
    </StyledMoreDiv>
  );
}
